import React, { useState, useEffect } from "react";
import "./VTRatio.css";
import { parseExpression } from "./utils/mathParser";

const VTRatio = ({ goBack, goToNext, formData, updateRatioData }) => {
  const primaryVoltages = [1000, 2000, 3000, 4000, 5000];
  const phases = ['R-ph', 'Y-ph', 'B-ph'];

  
  const STORAGE_KEY = 'vtRatioTestData';

  const createInitialData = () => {
    const result = {};
    primaryVoltages.forEach(v => {
      result[v] = phases.reduce((acc, phase) => {
        acc[phase] = { core: '', error: '' };
        return acc;
      }, {});
    });
    return result;
  };

  const getCoreCountFromWinding = (winding) => {
    if (!winding) return 0;
    if (winding.startsWith("da")) return 5;
    const match = winding.match(/^(\d)/);
    return match ? parseInt(match[1]) : 0;
  };

  
  const loadDataFromStorage = () => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        return JSON.parse(savedData);
      }
    } catch (error) {
      console.error('Error loading data from localStorage:', error);
    }
    return null;
  };

 
  const saveDataToStorage = (dataToSave) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Error saving data to localStorage:', error);
    }
  };

  const winding = formData.winding;
  const coreCount = getCoreCountFromWinding(winding);
  const windingDetails = formData.windingDetails || [];

 
  const [data, setData] = useState(() => {
    
    const savedData = loadDataFromStorage();
    if (savedData && Object.keys(savedData).length > 0) {
      return savedData;
    }
    
   
    if (Object.keys(formData.ratioTestData || {}).length > 0) {
      return formData.ratioTestData;
    }
    
   
    const initialData = {};
    for (let i = 1; i <= coreCount; i++) {
      initialData[i] = createInitialData();
    }
    return initialData;
  });

 
  useEffect(() => {
    saveDataToStorage(data);
  }, [data]);

  const calculateActualValue = (coreNum, voltage) => {
    if (coreNum <= 0 || coreNum > windingDetails.length) return null;
    
    const windingDetail = windingDetails[coreNum - 1];
    if (!windingDetail || !windingDetail.ratio || !windingDetail.volts) return null;
    
    try {
      const primary = parseExpression(windingDetail.ratio);
      const secondary = parseExpression(windingDetail.volts);
      
      if (isNaN(primary) || isNaN(secondary) || primary === 0) return null;
      
      const result = (secondary / primary) * voltage;
      return parseFloat(result.toFixed(3));
    } catch (error) {
      console.error("Error calculating actual value:", error);
      return null;
    }
  };

  useEffect(() => {
    if (windingDetails.length > 0) {
      const newData = { ...data };
      
      for (let i = 0; i < coreCount && i < windingDetails.length; i++) {
        const coreNum = i + 1;
        
        primaryVoltages.forEach(voltage => {
          phases.forEach(phase => {
            if (!newData[coreNum]) {
              newData[coreNum] = createInitialData();
            }
            
            if (!newData[coreNum][voltage]) {
              newData[coreNum][voltage] = {};
            }
            
            if (!newData[coreNum][voltage][phase]) {
              newData[coreNum][voltage][phase] = { core: '', error: '' };
            }
            
            const coreValue = newData[coreNum][voltage][phase].core;
            if (coreValue && coreValue !== '') {
              const actualValue = calculateActualValue(coreNum, voltage);
              if (actualValue !== null) {
                const measuredValue = parseFloat(coreValue);
                if (!isNaN(measuredValue)) {
                  const errorPercentage = Math.abs(((measuredValue - actualValue) / actualValue) * 100);
                  newData[coreNum][voltage][phase].error = errorPercentage.toFixed(2);
                }
              }
            }
          });
        });
      }
      
      setData(newData);
    }
  }, [windingDetails, coreCount]);

  const validateInput = (value) => {
    return value.replace(/[^0-9.-]/g, '');
  };

  const handleCoreValueChange = (coreNum, voltage, phase, value) => {
    const validatedValue = validateInput(value);
    
    const newData = { ...data };
    
    if (!newData[coreNum]) {
      newData[coreNum] = createInitialData();
    }
    
    if (!newData[coreNum][voltage]) {
      newData[coreNum][voltage] = {};
    }
    
    if (!newData[coreNum][voltage][phase]) {
      newData[coreNum][voltage][phase] = { core: '', error: '' };
    }
    
    newData[coreNum][voltage][phase].core = validatedValue;
    
    const actualValue = calculateActualValue(coreNum, voltage);
    
    if (actualValue !== null && validatedValue !== '') {
      const measuredValue = parseFloat(validatedValue);
      
      if (!isNaN(measuredValue)) {
        const errorPercentage = Math.abs(((measuredValue - actualValue) / actualValue) * 100);
        newData[coreNum][voltage][phase].error = errorPercentage.toFixed(2);
      } else {
        newData[coreNum][voltage][phase].error = '';
      }
    } else {
      newData[coreNum][voltage][phase].error = '';
    }
    
    setData(newData);
  };

  const handleErrorChange = (coreNum, voltage, phase, value) => {
    setData(prev => {
      const newData = { ...prev };
      
      if (!newData[coreNum]) {
        newData[coreNum] = createInitialData();
      }
      
      if (!newData[coreNum][voltage]) {
        newData[coreNum][voltage] = phases.reduce((acc, p) => {
          acc[p] = { core: '', error: '' };
          return acc;
        }, {});
      }
      
      if (!newData[coreNum][voltage][phase]) {
        newData[coreNum][voltage][phase] = { core: '', error: '' };
      }
      
      return {
        ...newData,
        [coreNum]: {
          ...newData[coreNum],
          [voltage]: {
            ...newData[coreNum][voltage],
            [phase]: {
              ...newData[coreNum][voltage][phase],
              error: value
            }
          }
        }
      };
    });
  };

  
  const clearStoredData = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      console.log('VT Ratio data cleared from localStorage');
    } catch (error) {
      console.error('Error clearing data from localStorage:', error);
    }
  };

const redirectToHome = () => {
  if (window.confirm("Are you sure you want to leave this page? Any unsaved changes will be lost.")) {  
    localStorage.setItem('openReportsOnLoad', 'true');
    window.location.href = "/";
  }
};

  const handleSubmit = (e) => {
    e.preventDefault();
    updateRatioData(data);
  
    goToNext();
  };

  const shouldHighlightError = (errorValue) => {
    if (!errorValue || errorValue === '') return false;
    const numValue = parseFloat(errorValue);
    return !isNaN(numValue) && numValue > 0;
  };

  return (
    <form className="vt-ratio-form" onSubmit={handleSubmit}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className="vt-ratio-title">RATIO TEST</h2>
        <span 
          style={{ 
            display: 'flex',
            justifyContent: 'flex-end',
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#333', 
            cursor: 'pointer',
            marginLeft: '50px',
            marginBottom: '50px'
          }}
          onClick={redirectToHome}
        >
          X
        </span>
      </div>

      {[...Array(coreCount)].map((_, i) => {
        const coreNum = i + 1;
        const windingDetail = windingDetails[i] || {};
        const { ratio, volts } = windingDetail;
        const hasWindingDetails = ratio && volts;
        
        return (
          <div key={coreNum} className="vt-ratio-section">
            <h3 className="vt-ratio-core-heading">CORE – {coreNum}</h3>
            
            {!hasWindingDetails && (
              <div className="vt-ratio-warning">
                No ratio and volts data found for Core {coreNum}. Please enter winding details first to enable error calculation.
              </div>
            )}

            <div className="vt-ratio-row vt-ratio-header-row">
              <div className="vt-ratio-voltage-header">
                Applied Voltage in Primary (Volts) <span className="vt-ratio-required">*</span>
              </div>
              <div className="vt-ratio-measured-header" style={{ gridColumn: "2 / span 6" }}>
                Measured Voltage in Secondary (Volts) <span className="vt-ratio-required">*</span>
              </div>
            </div>

            <div className="vt-ratio-row vt-ratio-phases-row">
              <div className="vt-ratio-voltage-header-placeholder" />
              {phases.map((phase, index) => (
                <div
                  key={phase}
                  className="vt-ratio-phase-header"
                  style={{ gridColumn: `${2 + index * 2} / span 2` }}
                >
                  {phase}
                </div>
              ))}
            </div>

            <div className="vt-ratio-row vt-ratio-sub-header-row">
              <div className="vt-ratio-voltage-header-placeholder" />
              {phases.map(phase => (
                <React.Fragment key={phase}>
                  <div className="vt-ratio-core-label">Core{coreNum}</div>
                  <div className="vt-ratio-error-label">% of error</div>
                </React.Fragment>
              ))}
            </div>

            {primaryVoltages.map(voltage => (
              <div key={voltage} className="vt-ratio-row">
                <div className="vt-ratio-voltage-cell">{voltage}</div>
                {phases.map(phase => {
                  const actualValue = calculateActualValue(coreNum, voltage);
                  const actualValueText = actualValue !== null 
                    ? `Expected value: ${actualValue.toFixed(3)}`
                    : 'No winding details available';
                  
                  const errorValue = data[coreNum]?.[voltage]?.[phase]?.error || "";
                  const isErrorHighlighted = shouldHighlightError(errorValue);
                    
                  return (
                    <React.Fragment key={phase}>
                      <input
                        type="text"
                        className="vt-ratio-core-input"
                        placeholder="Enter Core"
                        value={data[coreNum]?.[voltage]?.[phase]?.core || ""}
                        maxLength={10}
                        onChange={e =>
                          handleCoreValueChange(coreNum, voltage, phase, e.target.value)
                        }
                        title={actualValueText}
                        inputMode="decimal"
                      />
                      <input
                        type="text"
                        className={`vt-ratio-error-input ${isErrorHighlighted ? 'error-highlighted' : ''}`}
                        placeholder="% of error"
                        value={errorValue}
                        onChange={e =>
                          handleErrorChange(coreNum, voltage, phase, e.target.value)
                        }
                        readOnly
                        style={isErrorHighlighted ? { color: 'red' } : {}}
                        title="This value is automatically calculated"
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            ))}
          </div>
        );
      })}

      <div className="vt-ratio-button-group">
        <button type="button" onClick={goBack}>Back</button>
        <button type="submit">Next</button>
        
      </div>
    </form>
  );
};

export default VTRatio;


